import { extractMentionIds, extractRoleMentionIds, type AttachmentDetailsInput, type CreatePollInput, type MessageDTO, type MessageTargetRef } from '@clanfire/shared';
import { ApiError, errorMessage } from '@/api/client';
import { messageApi, pollApi } from '@/api/endpoints';
import { useAuth } from '@/stores/auth';
import { useGettingStarted } from '@/stores/gettingStarted';
import { keyOfMessage, targetKey, useMessages, type ClientMessage } from '@/stores/messages';
import { confirm, toast } from '@/stores/ui';
import { useSlowMode } from './slowMode';
import { randomId } from './utils';

/**
 * Sends a message optimistically: it appears immediately (dimmed), then is
 * swapped for the server's copy — matched by `nonce` — when it's saved.
 */
export async function sendMessage(
  target: MessageTargetRef,
  input: {
    content: string;
    files: File[];
    /** Each file's spoiler flag and description, in the same order as `files`. */
    attachments?: AttachmentDetailsInput[];
    replyTo: MessageDTO | null;
    replyPing?: boolean;
    serverId?: string;
    /** The channel's slow mode, when it applies to you: the wait starts once the message is sent. */
    slowModeSeconds?: number;
    /** A voice message: its one file is the recording. */
    voice?: { durationMs: number; waveform: string };
  },
) {
  const me = useAuth.getState().user;
  if (!me) return;
  const nonce = randomId();
  const optimistic: ClientMessage = {
    id: `pending-${nonce}`,
    type: 'DEFAULT',
    channelId: target.channelId ?? null,
    conversationId: target.conversationId ?? null,
    serverId: input.serverId ?? null,
    author: me,
    content: input.content,
    createdAt: new Date().toISOString(),
    editedAt: null,
    replyToId: input.replyTo?.id ?? null,
    replyPing: input.replyPing ?? true,
    pinnedAt: null,
    replyTo: input.replyTo
      ? { id: input.replyTo.id, author: input.replyTo.author, content: input.replyTo.content, hasAttachments: input.replyTo.attachments.length > 0 }
      : null,
    mentions: extractMentionIds(input.content),
    mentionRoleIds: extractRoleMentionIds(input.content),
    // The server decides whether @everyone pings (it needs Mention @everyone).
    mentionEveryone: false,
    emojiUrls: null,
    attachments: input.files.map((f, i) => ({
      id: `local-${i}`,
      fileName: f.name,
      // Images (and your own voice message) show straight away, before they're uploaded.
      url: f.type.startsWith('image/') || input.voice ? URL.createObjectURL(f) : '',
      contentType: f.type || 'application/octet-stream',
      size: f.size,
      width: null,
      height: null,
      durationMs: input.voice?.durationMs ?? null,
      waveform: input.voice?.waveform ?? null,
      spoiler: !!input.attachments?.[i]?.spoiler,
      description: input.attachments?.[i]?.description || null,
    })),
    reactions: [],
    poll: null,
    thread: null,
    forwarded: null,
    nonce,
    pending: true,
    localFiles: input.files,
  };
  useMessages.getState().addPending(optimistic);

  try {
    const saved = await messageApi.send(target, { content: input.content, replyToId: input.replyTo?.id ?? null, replyPing: input.replyPing, nonce, files: input.files, attachments: input.attachments, voice: input.voice });
    useMessages.getState().receive(saved);
    useGettingStarted.getState().markStep('message');
    if (target.channelId && input.slowModeSeconds) useSlowMode.getState().start(target.channelId, input.slowModeSeconds);
  } catch (err) {
    // Too soon in slow mode: the server says how long to wait.
    if (err instanceof ApiError && err.code === 'SLOW_MODE' && target.channelId) useSlowMode.getState().start(target.channelId, Number(err.fields?.retryAfter) || 0);
    useMessages.getState().failPending(targetKey(target), optimistic.id, errorMessage(err, 'Message failed to send.'));
  } finally {
    for (const a of optimistic.attachments) if (a.url.startsWith('blob:')) setTimeout(() => URL.revokeObjectURL(a.url), 60_000);
  }
}

/** Posts a poll (a message of its own). Returns false if it could not be created. */
export async function createPoll(target: MessageTargetRef, input: CreatePollInput): Promise<boolean> {
  try {
    useMessages.getState().receive(await pollApi.create(target, input));
    return true;
  } catch (err) {
    toast.error(errorMessage(err, 'Could not create the poll.'));
    return false;
  }
}

/** Sets your answers to a poll (none takes your vote back). */
export async function votePoll(message: MessageDTO, optionIds: string[]) {
  try {
    useMessages.getState().update(await pollApi.vote(message.id, optionIds));
  } catch (err) {
    toast.error(errorMessage(err, 'Could not record your vote.'));
  }
}

export async function endPoll(message: MessageDTO) {
  const ok = await confirm({ title: 'End the poll now?', body: 'Voting will close and everyone will see the final results.', confirmLabel: 'End poll' });
  if (!ok) return;
  try {
    useMessages.getState().update(await pollApi.end(message.id));
  } catch (err) {
    toast.error(errorMessage(err, 'Could not end the poll.'));
  }
}

export function retryMessage(message: ClientMessage) {
  const target: MessageTargetRef = message.channelId ? { channelId: message.channelId } : { conversationId: message.conversationId };
  useMessages.getState().removeLocal(keyOfMessage(message), message.id);
  const replyTo = message.replyTo
    ? ({ ...message, id: message.replyTo.id, author: message.replyTo.author, content: message.replyTo.content, attachments: [] } as MessageDTO)
    : null;
  const recording = message.attachments[0];
  const voice = recording?.durationMs != null ? { durationMs: recording.durationMs, waveform: recording.waveform ?? '' } : undefined;
  const attachments = message.attachments.map((a) => ({ spoiler: !!a.spoiler, description: a.description ?? undefined }));
  void sendMessage(target, { content: message.content, files: message.localFiles ?? [], attachments, replyTo, serverId: message.serverId ?? undefined, voice });
}

export async function toggleReaction(message: MessageDTO, emoji: string) {
  const me = useAuth.getState().user?.id;
  if (!me || message.id.startsWith('pending-')) return;
  const key = keyOfMessage(message);
  const reacted = message.reactions.find((r) => r.emoji === emoji)?.userIds.includes(me) ?? false;
  useMessages.getState().reaction(key, message.id, emoji, me, !reacted);
  try {
    if (reacted) await messageApi.unreact(message.id, emoji);
    else await messageApi.react(message.id, emoji);
  } catch (err) {
    useMessages.getState().reaction(key, message.id, emoji, me, reacted); // roll back
    toast.error(errorMessage(err, 'Could not update reaction.'));
  }
}

/** Deletes a message, asking for confirmation unless `skipConfirm` (Shift+click). */
export async function deleteMessage(message: MessageDTO, skipConfirm = false) {
  if (!skipConfirm) {
    const ok = await confirm({
      title: 'Delete message',
      body: 'Are you sure you want to delete this message? This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
  }
  const key = keyOfMessage(message);
  try {
    await messageApi.remove(message.id);
    useMessages.getState().remove(key, message.id);
  } catch (err) {
    toast.error(errorMessage(err, 'Could not delete the message.'));
  }
}

export async function setPinned(message: MessageDTO, pinned: boolean): Promise<boolean> {
  try {
    const updated = await (pinned ? messageApi.pin(message.id) : messageApi.unpin(message.id));
    useMessages.getState().update(updated);
    toast.success(pinned ? 'Message pinned.' : 'Message unpinned.');
    return true;
  } catch (err) {
    toast.error(errorMessage(err, pinned ? 'Could not pin the message.' : 'Could not unpin the message.'));
    return false;
  }
}

export async function editMessage(message: MessageDTO, content: string): Promise<boolean> {
  try {
    const updated = await messageApi.edit(message.id, content);
    useMessages.getState().update(updated);
    return true;
  } catch (err) {
    toast.error(errorMessage(err, 'Could not edit the message.'));
    return false;
  }
}
